// FrequentAccessCard — Home "Frequent Access" card.
// design ref: home.jsx FrequentAccessCard + styles.css:756–828 (.freq-row).
//
// Logic ported from the extension's useFrequentRequests: requests are tracked
// client-side (localStorage) whenever the member submits one, then the top few
// surface here for one-tap renewal. Items the member already has access to (or
// has a pending request for) are filtered out. Empty until the member has
// requested something.

import { Box, Flex, Icon, Text, useToast } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { TbCirclePlus, TbHistory } from "react-icons/tb";

import AppLogo from "components/ds/AppLogo";
import Card from "components/ds/Card";
import CardHeader from "components/ds/CardHeader";
import EmptyState from "components/ds/EmptyState";
import UtilityButton from "components/ds/UtilityButton";
import { RequestAccessModal } from "components/RequestAccess";
import { SINGULARIZE } from "components/RequestAccess/payloads";
import { useMemberAuthStore } from "modules/members/auth";
import { MF_ACCESS } from "state/members/access_v2/catalogs";
import { draftIdsToSign } from "state/members/stepUp";
import { useRequestSigning } from "pages/Members/StepUp/useRequestSigning";
import {
  useFrequentRequests,
  type FrequentRequest,
} from "state/members/frequentRequests";
import { frequentRowTitle } from "./frequentRowTitle";
import { resourcesRenewedInModal } from "./renewInModal";

// "Last requested: 2 days ago" from a ms timestamp (full words, per the design).
const lastRequested = (ts: number): string => {
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? "s" : ""} ago`;
  return `${Math.floor(months / 12)} year${Math.floor(months / 12) !== 1 ? "s" : ""} ago`;
};

const FreqRow = ({
  fr,
  submitting,
  disabled,
  onRenew,
}: {
  fr: FrequentRequest;
  submitting: boolean;
  disabled: boolean;
  onRenew: () => void;
}) => {
  const title = frequentRowTitle(fr);
  return (
    <Flex
      align="center"
      gap="md" // 12px
      p="md" // 12px
      borderWidth="1px"
      borderStyle="solid"
      borderColor="neutral.200"
      borderRadius="component.xl" // 10px
      bg="neutral.25"
      transition="border-color 120ms"
      _hover={{ borderColor: "neutral.300" }}
    >
      <AppLogo size="md" name={fr.appName} logoUrl={fr.appIcon} />
      <Box flex="1" minW={0}>
        {/* .freq-row__title — 14/1.4; app neutral.700, resource neutral.950/600 */}
        <Text fontSize="md" lineHeight="1.4" noOfLines={1}>
          {title.app && (
            <Box as="span" color="neutral.700">
              {title.app} /
            </Box>
          )}
          <Box
            as="span"
            color="neutral.950"
            fontWeight={600}
            ml={title.app ? "2xs" : undefined}
          >
            {title.resource}
          </Box>
        </Text>
        {/* .freq-row__meta — 12px, label neutral.700, value neutral.900/500 */}
        <Text fontSize="xs" color="neutral.700" noOfLines={1} mt="2xs">
          Last requested:{" "}
          <Box as="span" color="neutral.900" fontWeight={500}>
            {lastRequested(fr.lastRequestedAt)}
          </Box>
        </Text>
      </Box>
      <UtilityButton
        flexShrink={0}
        size="md"
        treatment="dropshadow"
        sentiment="azurite"
        rightIcon={<Icon as={TbCirclePlus} boxSize="14px" />}
        isLoading={submitting}
        isDisabled={disabled}
        onClick={onRenew}
      >
        Renew
      </UtilityButton>
    </Flex>
  );
};

const FrequentAccessCard = () => {
  const memberId = useMemberAuthStore((s) => s.id);
  const toast = useToast();
  const { getTopRequests, trackRequest } = useFrequentRequests();
  const managed = MF_ACCESS.useListManagedAccess();
  const myRequests = MF_ACCESS.useListMyActiveRequests();
  const catalogs = MF_ACCESS.useListAccessCatalogs();
  const { mutate: createRequest } = MF_ACCESS.useCreateAccessRequest();
  // A policy that wants the requester's passkey creates a draft instead.
  const followUpSigning = useRequestSigning();
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [modalFor, setModalFor] = useState<FrequentRequest | null>(null);

  const renewedInModalIds = useMemo(
    () => resourcesRenewedInModal(catalogs),
    [catalogs],
  );

  const activeAccessIds = useMemo(
    () =>
      new Set(
        managed.data
          .map((a) => a.selectableResourceId)
          .filter((id): id is string => Boolean(id)),
      ),
    [managed.data],
  );
  const activeRequestIds = useMemo(
    () =>
      new Set(
        myRequests.data
          .map((r) => r.selectableResource?.id)
          .filter((id): id is string => Boolean(id)),
      ),
    [myRequests.data],
  );

  const frequent = getTopRequests(5, activeAccessIds, activeRequestIds);

  const quickRequest = (fr: FrequentRequest) => {
    if (!memberId || submittingId) return;
    if (renewedInModalIds.has(fr.selectableResourceId)) {
      setModalFor(fr);
      return;
    }
    setSubmittingId(fr.selectableResourceId);
    createRequest({
      catalogItemId: fr.catalogItemId,
      selectableResourceId: fr.selectableResourceId,
      accessRequestedForMemberId: memberId,
      indefiniteRequest: Boolean(fr.lastIndefinite),
      requestedForUnit:
        fr.lastIndefinite || !fr.lastDuration
          ? undefined
          : (SINGULARIZE[fr.lastDuration.unit] ?? fr.lastDuration.unit),
      requestedForValue: fr.lastIndefinite ? undefined : fr.lastDuration?.value,
      comments: fr.lastComments || undefined,
    })
      .then(async (res) => {
        // Bump the count + refresh recency (the row then drops off as the new
        // pending request lands in activeRequestIds).
        trackRequest({
          selectableResourceId: fr.selectableResourceId,
          catalogItemId: fr.catalogItemId,
          catalogId: fr.catalogId,
          catalogItemName: fr.catalogItemName,
          appName: fr.appName,
          appIcon: fr.appIcon,
          resourceName: fr.resourceName,
          rawResourceName: fr.rawResourceName,
          lastDuration: fr.lastDuration,
          lastIndefinite: fr.lastIndefinite,
          lastComments: fr.lastComments,
        });
        const drafts = draftIdsToSign([res?.success ? res.data : null]);
        if (await followUpSigning(drafts)) return;
        toast({
          status: "success",
          title: "Access requested",
          description: `You requested ${fr.resourceName}.`,
        });
      })
      // useCreateAccessRequest toasts every failure; swallow so it's not unhandled.
      .catch(() => undefined)
      .finally(() => setSubmittingId(null));
  };

  if (frequent.length === 0) {
    return (
      <Card>
        <CardHeader title="Frequent Access" />
        <EmptyState
          icon={<TbHistory />}
          title="Nothing frequent yet"
          body="Items you request often will show up here for one-tap renewal."
        />
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader title="Frequent Access" />
        <Box display="flex" flexDirection="column" gap="md">
          {frequent.map((fr) => (
            <FreqRow
              key={fr.selectableResourceId}
              fr={fr}
              submitting={submittingId === fr.selectableResourceId}
              disabled={Boolean(submittingId)}
              onRenew={() => quickRequest(fr)}
            />
          ))}
        </Box>
      </Card>
      {modalFor && (
        <RequestAccessModal
          isOpen
          onClose={() => setModalFor(null)}
          initialCatalogId={modalFor.catalogId}
          initialCatalogItemId={modalFor.catalogItemId}
          initialBasket={[
            `${modalFor.catalogId}__${modalFor.catalogItemId}__${modalFor.selectableResourceId}`,
          ]}
        />
      )}
    </>
  );
};

export default FrequentAccessCard;
